import './style.css'

import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

interface Option {
  id: string
  textKey: string
  value: string
}

export interface SelectProps {
  id: string
  textKey: string
  value?: Option['value']
  options: Option[]
  onChange: (value: Option) => void
  placeholder?: string
  required?: boolean
  disabled?: boolean
  error?: string
  tabIndex?: number
}

export const Select = ({ id, textKey, value, options, onChange, required, placeholder, disabled, error, tabIndex }: SelectProps) => {
  const { t } = useTranslation()
  const [selected, setSelected] = useState<Option | null>(() => options.find((option) => option.value === value) || null)

  useEffect(() => {
    if (!selected) return
    onChange(selected)
  }, [selected])

  return (
    <div className="flex w-full flex-col gap-1" data-testid={`${id}-container`}>
      <label data-testid={`${id}-label`} htmlFor={id} className="font-bold">
        {t(textKey)}
        {required && <span> *</span>}
      </label>

      <select
        id={id}
        data-testid={`${id}-select`}
        tabIndex={tabIndex}
        disabled={disabled}
        value={value ?? ''}
        required={required}
        onChange={(e) => {
          setSelected(options.find((option) => option.value === e.target.value) || null)
        }}
        className={`w-full rounded-md border p-4 ${
          error ? 'border-error' : 'border-gray'
        } bg-white focus-visible:outline-primary disabled:cursor-not-allowed disabled:bg-gray/50`}
      >
        {placeholder && (
          <option value="" disabled>
            {t(placeholder)}
          </option>
        )}

        {options.map((option) => (
          <option id={option.id} key={option.id} value={option.value}>
            {t(option.textKey)}
          </option>
        ))}
      </select>

      {error && (
        <span className="text-sm text-error" data-testid={`${id}-error`}>
          {t(error)}
        </span>
      )}
    </div>
  )
}
